import { ApiError } from "@/lib/api"
import { formatCurrency } from "@/lib/utils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, type ReactNode, useState } from "react"
import { type EnvioDeAlegacao, enviarAlegacao } from "../api"
import type { Repasse } from "../schema"
import { FOCO } from "./foco"

const BOTAO = `min-h-11 rounded-full bg-brand px-6 py-2.5 font-display font-semibold text-brand-ink transition-colors hover:bg-brand-strong disabled:opacity-60 ${FOCO}`

const arquivos = (form: HTMLFormElement, campo: string) =>
    Array.from((form.elements.namedItem(campo) as HTMLInputElement).files ?? [])

const problemaDo = (contratos: string[], relatorio: File | undefined) => {
    if (contratos.length === 0) return "Marque pelo menos um contrato."
    if (!relatorio) return "Falta o relatório de empréstimos consignados."
    return null
}

const mensagemDe = (erro: unknown) =>
    erro instanceof ApiError && erro.mensagem
        ? erro.mensagem
        : "Não conseguimos registrar o envio. Recarregue a página: se os contratos aparecerem como Em análise, está feito."

const CampoArquivo = ({
    nome,
    rotulo,
    ajuda,
    obrigatorio = false,
}: { nome: string; rotulo: string; ajuda: ReactNode; obrigatorio?: boolean }) => (
    <div className="flex flex-col gap-1.5">
        <label htmlFor={`campo-${nome}`} className="font-semibold">
            {rotulo}
        </label>
        <p id={`ajuda-${nome}`} className="max-w-[66ch] text-sm text-soft [&_b]:text-ink">
            {ajuda}
        </p>
        <input
            id={`campo-${nome}`}
            name={nome}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple={!obrigatorio}
            aria-required={obrigatorio}
            aria-describedby={`ajuda-${nome}`}
            className={`min-h-11 text-sm text-soft file:mr-3 file:rounded-full file:border-0 file:bg-brand file:px-4 file:py-2 file:font-semibold file:text-brand-ink ${FOCO}`}
        />
    </div>
)

interface Props {
    repasse: Repasse
    caminho: string
}

const Formulario = ({ repasse, caminho, onEnviada }: Props & { onEnviada: () => void }) => {
    const queryClient = useQueryClient()
    const [problema, setProblema] = useState<string | null>(null)

    const mutation = useMutation({
        mutationFn: (envio: EnvioDeAlegacao) => enviarAlegacao(repasse.id, envio),
        onSuccess: (novo) => {
            queryClient.setQueryData(["repasse", caminho], novo)
            onEnviada()
        },
    })

    const enviar = (evento: FormEvent<HTMLFormElement>) => {
        evento.preventDefault()
        const form = evento.currentTarget
        const contratos = new FormData(form).getAll("contrato").map(String)
        const [relatorio] = arquivos(form, "relatorio")
        const comprovantes = arquivos(form, "comprovante")
        const encontrado = problemaDo(contratos, relatorio)
        setProblema(encontrado)
        if (encontrado || !relatorio) return
        mutation.mutate({ contratos, relatorio, comprovantes })
    }

    const mensagem = problema ?? (mutation.isError ? mensagemDe(mutation.error) : null)

    return (
        <form id="alegacao" noValidate onSubmit={enviar} className="mt-5 flex flex-col gap-6">
            <fieldset className="flex flex-col gap-1 rounded-lg border border-line bg-card p-4">
                <legend className="px-1 font-semibold">Quais contratos você repassou?</legend>
                {repasse.contratos
                    .filter((contrato) => contrato.alegavel)
                    .map((contrato) => (
                        <label
                            key={contrato.id}
                            className="flex min-h-11 cursor-pointer flex-wrap items-center gap-x-3 rounded-lg px-2 py-2 text-sm hover:bg-sunk"
                        >
                            <input
                                type="checkbox"
                                name="contrato"
                                value={contrato.id}
                                defaultChecked={contrato.situacao === "NAO_IDENTIFICADO"}
                                className={`size-4 accent-brand ${FOCO}`}
                            />
                            <span className="min-w-0 flex-1">
                                <span className="font-semibold">{contrato.colaborador}</span>{" "}
                                <span className="font-mono text-xs text-soft">
                                    CPF {contrato.cpfMascarado} · Contrato{" "}
                                    {contrato.contrato || "—"}
                                </span>
                            </span>
                            <span className="font-semibold tabular-nums">
                                {formatCurrency(contrato.valor)}
                            </span>
                        </label>
                    ))}
            </fieldset>

            <CampoArquivo
                nome="relatorio"
                rotulo="Relatório de empréstimos consignados"
                obrigatorio
                ajuda={
                    <>
                        No FGTS Digital, entre em <b>Gestão de Guias › Consulta de Guias</b>, clique
                        em <b>Detalhar</b> na guia paga, depois em <b>Imprimir relatório PDF</b>, e
                        abra a aba <b>Consignado</b>. É o relatório que lista CPF, número do
                        contrato e instituição de cada colaborador.
                    </>
                }
            />
            <CampoArquivo
                nome="comprovante"
                rotulo="Comprovante de pagamento (opcional)"
                ajuda="A guia paga com a autenticação bancária, ou o comprovante do Pix. Até 2 arquivos."
            />

            <p className="max-w-[70ch] text-xs text-soft">
                Os arquivos trazem dados de contratos dos seus colaboradores, inclusive de outras
                instituições. Usamos só para conferir o repasse.
            </p>

            {mensagem && (
                <p role="alert" className="rounded-lg bg-off-wash px-4 py-3 text-sm text-off">
                    {mensagem}
                </p>
            )}

            <button type="submit" disabled={mutation.isPending} className={`self-start ${BOTAO}`}>
                {mutation.isPending ? "Enviando…" : "Enviar para análise"}
            </button>
        </form>
    )
}

export const ChamadaAlegacao = ({ repasse, caminho }: Props) => {
    const [aberta, setAberta] = useState(false)

    if (!repasse.podeAlegar) return null

    return (
        <section
            aria-labelledby="chamada-alegacao"
            className="border-b border-line bg-brand-wash px-6 py-4"
        >
            <h2 id="chamada-alegacao" className="font-semibold">
                Repassou e não identificamos?
            </h2>
            <button
                type="button"
                aria-expanded={aberta}
                aria-controls="alegacao"
                onClick={() => setAberta(!aberta)}
                className={`mt-3 ${BOTAO}`}
            >
                Envie sua escrituração aqui
            </button>
            {aberta && (
                <Formulario
                    repasse={repasse}
                    caminho={caminho}
                    onEnviada={() => setAberta(false)}
                />
            )}
        </section>
    )
}
